import { Box, HStack, Tooltip, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';

import GradientTooltip from 'components/GradientTooltip';
import Icon from 'components/Icon';
import Text from 'components/Text';

import { calculateCurrentStreak, getDaysToShow } from './dashboardUtils';

interface ActivityHeatmapProps {
  activityDates: string[];
}

const CELL_SIZE = 12;
const GAP = 2;

const INTENSITY_COLORS = [
  'whiteAlpha.50',
  'purple.500',
  'purple.400',
  'purple.300',
  'purple.200',
];

const getIntensityColor = (count: number): string => {
  if (count === 0) return INTENSITY_COLORS[0];
  if (count === 1) return INTENSITY_COLORS[1];
  if (count <= 3) return INTENSITY_COLORS[2];
  if (count <= 6) return INTENSITY_COLORS[3];
  return INTENSITY_COLORS[4];
};

const DAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', ''];

const StreakTooltipLabel = () => (
  <VStack spacing={1.5} align="start">
    <Text fontSize="micro" fontWeight="semibold">
      What counts for your streak:
    </Text>
    <VStack spacing={1} align="start" pl={1}>
      <Text fontSize="micro" opacity="secondary">
        &bull; Complete a project card
      </Text>
      <Text fontSize="micro" opacity="secondary">
        &bull; Complete a project, challenge, or problem
      </Text>
      <Text fontSize="micro" opacity="secondary">
        &bull; Create a prototype or code frame
      </Text>
      <Text fontSize="micro" opacity="secondary">
        &bull; Create a CV
      </Text>
    </VStack>
    <Text fontSize="micro" opacity="tertiary">
      One qualifying activity per day keeps your streak alive!
    </Text>
  </VStack>
);

const ActivityHeatmap = ({ activityDates }: ActivityHeatmapProps) => {
  const daysToShow = getDaysToShow();
  const streak = useMemo(() => calculateCurrentStreak(activityDates), [activityDates]);

  const { grid, monthLabels } = useMemo(() => {
    const dayCounts = new Map<string, number>();
    for (const dateStr of activityDates) {
      const day = dateStr.slice(0, 10);
      dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
    }

    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - daysToShow);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const cells: { date: string; count: number; dayOfWeek: number; weekIndex: number }[] = [];
    const months: { label: string; weekIndex: number }[] = [];
    let lastMonth = -1;

    const cursor = new Date(startDate);
    let weekIndex = 0;

    while (cursor <= today) {
      const dayKey = cursor.toISOString().slice(0, 10);
      const dayOfWeek = cursor.getDay();

      if (dayOfWeek === 0 && cursor > startDate) weekIndex++;

      const month = cursor.getMonth();
      if (month !== lastMonth) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        months.push({ label: monthNames[month], weekIndex });
        lastMonth = month;
      }

      cells.push({ date: dayKey, count: dayCounts.get(dayKey) || 0, dayOfWeek, weekIndex });
      cursor.setDate(cursor.getDate() + 1);
    }

    const totalWeeks = weekIndex + 1;
    const gridData: (typeof cells[0] | null)[][] = Array.from({ length: totalWeeks }, () =>
      Array.from({ length: 7 }, () => null)
    );

    for (const cell of cells) {
      gridData[cell.weekIndex][cell.dayOfWeek] = cell;
    }

    // Filter out month labels that are too close together (< 3 weeks apart) to avoid overlap
    const filteredMonths = months.filter((m, i) => {
      if (i === 0) return true;
      return m.weekIndex - months[i - 1].weekIndex >= 3;
    });

    return { grid: gridData, monthLabels: filteredMonths };
  }, [activityDates, daysToShow]);

  return (
    <Box
      w="100%"
      p={5}
      borderRadius="xl"
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
    >
      {/* Header with stats */}
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Text fontSize="large" fontWeight="semibold">
            Activity
          </Text>
          <GradientTooltip label={<StreakTooltipLabel />} placement="top">
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w="16px"
              h="16px"
              borderRadius="full"
              bg="whiteAlpha.100"
              borderWidth={1}
              borderColor="whiteAlpha.200"
              cursor="help"
              _hover={{ bg: 'whiteAlpha.200', borderColor: 'whiteAlpha.300' }}
              transition="all 0.15s"
            >
              <Text style={{ fontSize: '10px', lineHeight: 1 }} opacity="secondary">
                ?
              </Text>
            </Box>
          </GradientTooltip>
        </HStack>
        <HStack spacing={1.5}>
          <Icon type="Lightning" size={14} color={streak > 0 ? '#ED8936' : undefined} />
          <Text fontSize="micro" fontWeight="semibold" opacity={streak > 0 ? 'primary' : 'tertiary'}>
            {streak > 0 ? `${streak} day streak` : 'No streak'}
          </Text>
        </HStack>
      </HStack>

      <Box overflowX="auto" display="flex" justifyContent="center">
        <VStack spacing={0} alignItems="flex-start" minW="fit-content">
          {/* Month labels */}
          <HStack spacing={0} mb={1} pl="28px">
            {monthLabels.map((m, i) => (
              <Box
                key={`${m.label}-${i}`}
                position="relative"
                style={{ left: `${m.weekIndex * (CELL_SIZE + GAP)}px` }}
              >
                <Text fontSize="micro" opacity="tertiary" position="absolute" whiteSpace="nowrap">
                  {m.label}
                </Text>
              </Box>
            ))}
          </HStack>

          <HStack spacing={0} mt={4}>
            {/* Day labels */}
            <VStack spacing={`${GAP}px`} mr={1} w="24px">
              {DAY_LABELS.map((label, i) => (
                <Box key={i} h={`${CELL_SIZE}px`} display="flex" alignItems="center">
                  <Text fontSize="micro" opacity="tertiary" style={{ fontSize: '9px' }}>
                    {label}
                  </Text>
                </Box>
              ))}
            </VStack>

            {/* Grid */}
            <HStack spacing={`${GAP}px`} alignItems="flex-start">
              {grid.map((week, wi) => (
                <VStack key={wi} spacing={`${GAP}px`}>
                  {week.map((cell, di) => (
                    <Tooltip
                      key={`${wi}-${di}`}
                      label={cell ? `${cell.count} activit${cell.count === 1 ? 'y' : 'ies'} on ${new Date(cell.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                      isDisabled={!cell}
                      placement="top"
                      hasArrow
                    >
                      <Box
                        w={`${CELL_SIZE}px`}
                        h={`${CELL_SIZE}px`}
                        borderRadius="2px"
                        bg={cell ? getIntensityColor(cell.count) : 'transparent'}
                        border={cell ? '1px solid' : 'none'}
                        borderColor="whiteAlpha.100"
                      />
                    </Tooltip>
                  ))}
                </VStack>
              ))}
            </HStack>
          </HStack>

          {/* Legend */}
          <HStack spacing={1} mt={3} justify="flex-end" w="100%">
            <Text fontSize="micro" opacity="tertiary" mr={1}>
              Less
            </Text>
            {INTENSITY_COLORS.map((color, i) => (
              <Box
                key={i}
                w={`${CELL_SIZE}px`}
                h={`${CELL_SIZE}px`}
                borderRadius="2px"
                bg={color}
                border="1px solid"
                borderColor="whiteAlpha.100"
              />
            ))}
            <Text fontSize="micro" opacity="tertiary" ml={1}>
              More
            </Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default ActivityHeatmap;
